import {filter, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {OnDestroyMixin} from '@w11k/ngx-componentdestroyed';
import {Sort} from '@angular/material/sort';
import {EMPTY} from 'rxjs';

import {DialogService} from 'src/app/services/dialog.service';
import {UserService} from 'src/app/services/shared/user.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import SectionsAccess from 'src/app/permission/enums/sectionsAccess.enum';
import PermissionLevel from 'src/app/permission/enums/permissionLevel.enum';
import {UserAndPermissionTableService} from './user-and-permission-table.service';
import {UserAndPermissionTableDataSource} from './UserAndPermissionTableDataSource';
import {UserPermissionDto} from 'src/app/permission/classes/userPermissionDto.models';
import {FinancialPermissionLevel} from 'src/app/permission/enums/financialPermissionLevel.enum';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import ContactsVisibilityPermission from 'src/app/permission/enums/contactsVisibilityPermission.enum';
import {ConfirmDialogParams} from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import {UserAndPermissionEditDialogComponent, UserAndPermissionEditDialogData} from '../user-and-permission-edit-dialog/user-and-permission-edit-dialog.component';
import {UtilsService} from 'src/app/services/utils.service';
import {BaseResponseDto} from '../../../shared/models/BaseResponseDto.model';
import {UserPermissionDataService} from '../../../services/gp/user-permission-data.service';
import ViewAsInvestorPermission from '../../../permission/enums/view-as-investor-permission';

@Component({
  selector: 'terra-user-and-permission-table',
  templateUrl: './user-and-permission-table.component.html',
  styleUrls: ['./user-and-permission-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAndPermissionTableComponent extends OnDestroyMixin implements OnInit {
  dataSource = new UserAndPermissionTableDataSource(this.userAndPermissionTableService);
  isLoading$ = this.dataSource.loading$;
  totalRowsCount$ = this.dataSource.totalRowsCount$;

  PermissionLevel = PermissionLevel;
  SectionsAccess = SectionsAccess;
  FinancialPermissionLevel = FinancialPermissionLevel;
  ContactsVisibilityPermission = ContactsVisibilityPermission;

  deleteSuccessMessage = 'Permission deleted';
  deleteFailureMessage = 'Permission delete failed';

  displayedColumns: string[] = ['name', 'email', 'permissionLevel', 'sections', 'holding', 'financialPermissions', 'contactsVisibility', 'action'];

  // selection = new SelectionModel<UserPermissionDto>(true, []);

  allHoldings$ = this.userPermissionDataService.getAllHoldings().pipe(
    map(response => response.rows || []));

  emailOfCurrentUser$ = this.userService.accountDetails$
    .pipe(
      map(clientDetails => clientDetails.userName),
      shareReplay(1)
    );

  constructor(
    private userAndPermissionTableService: UserAndPermissionTableService,
    private userPermissionDataService: UserPermissionDataService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private userService: UserService,
    private utilsService: UtilsService,
    private snackbarService: SnackbarService
  ) {
    super();
  }

  ngOnInit(): void {
  }


  onDeleteRow(user: UserPermissionDto) {
    const currentUser = this.userService.getClientDetailsFromLocalStorage();
    if (currentUser.userPermission.permissionLevel !== PermissionLevel.Admin) {
      return;
    }
    const dialogParams = new ConfirmDialogParams();
    dialogParams.panelClass = 'delete-contact-dialog';
    dialogParams.actionLabel = `Delete`;
    dialogParams.title = `Are you sure you want to delete this user?`;
    dialogParams.description = `You cannot undo this action.`;

    this.emailOfCurrentUser$.pipe(
      take(1),
      filter(email => email !== user.email),
      switchMap(_ => this.dialogService.confirmDialog(dialogParams).afterClosed().pipe(take(1))),
      filter(confirmed => !!confirmed),
      switchMap(_ => this.userAndPermissionTableService.deleteUserPermission(user)),
      tap(_ => this.userAndPermissionTableService.refreshData())
    ).subscribe(
      // _ => this.snackbarService.showGeneralMessage(this.deleteSuccessMessage),
      // err => this.snackbarService.showGeneralMessage(this.deleteFailureMessage)
      response => {
        this.snackbarService.showGeneralMessage('User removed');
      },
      err => {
        if ('responseStatus' in err?.error) {
          this.utilsService.alertErrorMessage(err.error as BaseResponseDto);
        } else {
          this.snackbarService.showGeneralMessage('Failed to remove user');
        }
      }
    );
  }

  trackByFunction(index: number, item: UserPermissionDto
  ) {
    return item.id;
  }

  sortData(sort: Sort) {
    if (!sort.active || !sort.direction) {
      return;
    }
    this.userAndPermissionTableService.sort(sort.active, sort.direction);
  }

  addNewUser(allHoldings: GpHoldingListItemReqRes[]) {
    const userPermissionDto = {
      name: null,
      email: null,
      permissionLevel: PermissionLevel.Editor,
      allowedSections: SectionsAccess.Assets | SectionsAccess.Funds | SectionsAccess.Insights | SectionsAccess.Positions | SectionsAccess.Contacts,
      financialPermission: FinancialPermissionLevel.Hidden,
      holdings: [...allHoldings],
      comment: null,
      contactsVisibility: ContactsVisibilityPermission.Visible,
    } as UserPermissionDto;

    this.openEditCreateUserDialog(userPermissionDto, allHoldings, true);

  }

  onEditRow(userPermissionDto: UserPermissionDto, allHoldings: GpHoldingListItemReqRes[]) {
    this.openEditCreateUserDialog(userPermissionDto, allHoldings, false);
  }

  private openEditCreateUserDialog(userPermissionDto: UserPermissionDto, allHoldings: GpHoldingListItemReqRes[], createMode: boolean) {
    const dialogConfig = new MatDialogConfig<UserAndPermissionEditDialogData>();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = false;

    dialogConfig.data = {
      userPermissionDto,
      allHoldings,
      createMode,
    };

    this.dialog.open(UserAndPermissionEditDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter(permission => !!permission),
        tap(_ => this.userAndPermissionTableService.refreshData()),
        switchMap(_ => !createMode ? this.userService.getClientDetails(true) : EMPTY)
      )
      .subscribe();
  }
}
